import apiClient from './api';

// Brand knowledge (Admeru CMO Phase 1): onboarding, facts and brand strategy.
const BASE = '/api/v1/brand-knowledge';

export interface Fact {
  id: string;
  ref: string;
  entity_type: string;
  entity_key: string;
  attribute: string;
  value: any;
  status: 'confirmed' | 'accepted' | 'proposed';
  source_kind: string;
  source_label: string;
  risk_class: 'A' | 'B' | 'C';
  confidence: number;
  label: string;
  vtype?: 'text' | 'str' | 'number' | 'list' | 'enum' | 'url' | 'bool';
  choices?: string[];                       // a pick-list (enum: one, list: several)
  choice_labels?: Record<string, string>;   // the choices in owner words
}

export interface EntityItem {
  id: string;
  key: string;
  facts: Record<string, Fact>;
  links: Record<string, string[]>;
}

export interface PendingFact {
  id: string;
  entity_type: string;
  entity_key: string;
  attribute: string;
  label: string;
  value: any;
  source_label: string;
  risk_class: 'A' | 'B' | 'C';
}

export interface Question {
  id: string;
  entity_type: string;
  entity_key: string | null;
  attribute: string;
  label: string;
  question: string;
  vtype: 'text' | 'str' | 'number' | 'list' | 'enum' | 'url' | 'bool' | 'connect';
  choices: string[];
  platform?: string;         // vtype 'connect': the ad platform to connect (or plan without past results)
  multi_entity?: boolean;
  suggested?: string;        // value already found (Brand Kit, website), waiting for confirmation
  suggested_from?: string;   // e.g. "on your website", "in Brand Kit"
  step?: string;             // onboarding step id (readiness.steps)
  optional?: boolean;        // improves the strategy; never blocks it
  choice_labels?: Record<string, string>;
}

export interface Basis {
  reason: string;
  source: 'owner' | 'website' | 'brand_fact' | 'recommendation' | 'assumption';
  confidence: 'high' | 'medium' | 'low';
  fact_refs: string[];
}

/** The same questions answered for every allowed channel, chosen or not. */
export interface ChannelCheck {
  platform: string;
  demand: string; reach: string; format_fit: string; path_to_goal: string; budget: string; past_results: string;
  verdict: 'use' | 'test_later' | 'skip';
  reason: string;
}

export interface OwnerComment { text: string; on_version: number; at: string }

/** A strategy or plan generation running in the background. */
export interface BrandJob {
  id: string;
  kind: 'strategy' | 'plan';
  status: 'queued' | 'running' | 'done' | 'failed';
  params: { month?: string; new_variant?: boolean };
  result_id: string | null;
  error: string | null;
}

/** Where the website disagrees with what the business already says. */
export interface WebsiteConflict { label: string; website: string; yours: string }
export type WebsiteDecision = 'use_website' | 'pick' | 'discard';

/** A read of the owner's website, running on the server (it carries on when they leave the page). */
export interface WebsiteRead {
  id: string;
  kind: 'website';
  status: 'queued' | 'running' | 'done' | 'failed';
  params: {
    url: string;
    found?: { accepted: number; proposed: number; warning: string; conflicts?: WebsiteConflict[] };
    decision?: WebsiteDecision;
  };
  error: string | null;
  created_at: string | null;
  finished_at: string | null;
}

/** One campaign being produced from an approved plan brief. */
export interface Production {
  id: string; plan_id: string | null;  // null: an own post that is in no plan
  kind: 'campaign' | 'post';
  brief_id: string;            // '' for a post (see post_id)
  post_id?: string | null;
  status: 'queued' | 'running' | 'needs_input' | 'ready' | 'failed' | 'paused' | 'cancelled';
  step: 'setup' | 'ads' | 'sizes' | 'caption' | 'checks' | 'ready'; step_label: string;
  question?: { id: string; type: 'confirm' | 'text' | 'choose'; text: string; options?: { value: string; label: string; url?: string }[] } | null;
  notes: { text: string; applied: boolean }[]; error?: string | null; campaign_id?: string | null;
  ads: { variant: number; format: string; kind: 'image' | 'video'; url: string; source: string }[];
  done: number; total: number; credits_estimate: number; credits_used: number;
  inputs?: { message?: string; segment?: string; offering?: string; markets?: string[]; route?: string;
             media?: { url: string; kind: string; label?: string }[] } | null;
  current?: { variant?: number; format?: string; kind?: 'image' | 'video'; stage: 'brief' | 'image' | 'compose' | 'size';
              headline?: string; subline?: string; cta?: string; idea?: string; image_url?: string; photo_url?: string } | null;
  minutes_left?: number | null;
  approved_at?: string | null;
  launch?: { type: 'now' | 'scheduled'; at: string | null; days: number } | null;
  schedule?: PostSchedule | null;      // a post: its scheduled publish
  caption?: string | null;
}
export interface PostSchedule { id: string; at: string; status: string; account: string; now?: boolean }
/** The browser's timezone, for "your time" and posting now. */
export const localZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
/** Where the month's posts go: one Facebook Page and/or one Instagram account. */
export interface PostAccount {
  platform: 'meta' | 'instagram'; connection_id: string; account_id: string; account_name?: string;
  page_id?: string; page_name?: string;
}
export interface PostDeliverable { format: string; kind: 'image' | 'video'; slides: number }
export interface PlanPostRow {
  id: string; key: string; status: 'suggested' | 'approved' | 'skipped'; version: number; name: string;
  post: PlanContent['organic_posts'][number] & { caption?: string };
  deliverable: PostDeliverable; approved_at: string | null;
  own?: boolean;                 // the owner defined it (New post)
  plan_id?: string | null;       // null: an own post in no plan
  month?: string | null;
}
/** An own post that is in no plan, with where it stands. */
export interface OwnPost { row: PlanPostRow; state: ItemState; production: Production | null }
/** The month's own posts in no plan, its posting accounts, and the approved plan they can join. */
export interface OwnPostsMonth {
  month: string; posts: OwnPost[]; accounts: PostAccount[]; months: string[];
  plan: { id: string; month: string } | null;
}
export interface OwnPostForm {
  channel: 'instagram_organic' | 'facebook_organic'; format: 'post' | 'carousel' | 'reel';
  date: string; time: string; idea: string; message?: string; caption?: string;
  add_to_plan: boolean; make: MediaChoice | null;
  post_now?: string | null;      // the owner's timezone: post as soon as it is made (own files + own caption)
}
export interface PostChecklist {
  post_id: string; name: string; channel: string; format: string; idea: string; date: string;
  deliverable: PostDeliverable; checks: { level: 'info' | 'warn' | 'block'; text: string }[];
  has_caption: boolean; month: string;
  media: { suggested: MediaChoice; estimates: Partial<Record<MediaMode, { ads: number; credits: number; minutes: number; note: string }>> };
}
/** One word for where an approved plan item stands (the daily sweep's view). */
export type ItemState = 'skipped' | 'suggested' | 'published' | 'scheduled' | 'failed' | 'approved' | 'ready'
  | 'making' | 'not_started' | 'missed' | 'to_confirm';
export interface PostReview {
  production: Production; kind: 'post';
  post: PlanContent['organic_posts'][number] & { name: string; deliverable: PostDeliverable };
  slides: { slide: number; format: string; kind: 'image' | 'video'; url: string; template_id?: string | null }[];
  caption: string;
  options: { id: 'idea' | 'caption'; label: string; credits: number }[];
  accounts: PostAccount[]; account: PostAccount | null;
  schedule: PostSchedule | null;
  warnings: string[];
  month: string; own: boolean; planned: boolean; post_id: string;
}
export interface ReviewOption { id: 'layout' | 'image' | 'idea'; label: string; credits: number; unavailable: string | null; note: string }
export interface ReviewConcept {
  id: string; variant: number; kind: 'image' | 'video'; source: string; included: boolean; on_canvas: boolean; regenerating: boolean;
  ads: { format: string; url: string; template_id?: string | null; source: string }[];
  options: ReviewOption[];
}
export interface ProductionReview {
  production: Production;
  brief: { name: string; channel: string; start_date: string; end_date: string; budget: number; currency?: string;
           offering?: string; message?: string; segment?: string; call_to_action?: string; creative_route?: CreativeRoute | null;
           deliverables?: { format: string; count: number; motion?: boolean }[] };
  stage: string;
  settings: { objective?: string; audience?: string; targeting: { age_range?: string; gender?: string; locations?: string[]; interests?: string[] };
              budget?: number; currency?: string; duration_days?: number; channels: string[]; headlines: string[]; texts: string[] };
  concepts: ReviewConcept[];
  warnings: string[];
}
export type MediaMode = 'variations' | 'as_is' | 'create';
export interface MediaChoice { mode: MediaMode; media_ids: string[] }
export interface ProductionChecklist {
  plan_id: string; month: string;
  items: { kind: 'campaign' | 'post'; brief_id: string; post_id?: string;
           // a post: its format, idea, date and what is made
           format?: string; idea?: string; date?: string; deliverable?: PostDeliverable;
           name: string; channel: string; route?: CreativeRoute | null; budget: number; start_date: string;
           deliverables: { format: string; count: number; motion?: boolean }[];
           estimate: { ads: number; credits: number; minutes: number; note: string };
           checks: { level: 'info' | 'warn' | 'block'; text: string }[];
           assets?: { id: string; url: string; kind: string; format?: string | null }[];
           media?: { suggested: MediaChoice; estimates: Record<MediaMode, { ads: number; credits: number; minutes: number; note: string }> } | null }[];
  totals: { campaigns: number; ads: number; credits: number; minutes: number };
  blocking: string[];
}

export interface StrategyContent {
  /** The first step the ads ask a new customer for, judged; a suggestion waits for the owner's yes. */
  offer_advice?: { current: string; verdict: 'keep' | 'strengthen' | 'add'; suggestion: string; why: string; test: string } | null;
  brand_summary: Basis & { value: string };
  positioning: Record<'target' | 'category' | 'main_benefit' | 'differentiator' | 'proof', Basis & { value: string }>;
  priority_segments: (Basis & { name: string; description: string; decision_maker: string; main_objections: string[] })[];
  offerings_to_lead: (Basis & { offering_ref: string; role: 'lead' | 'support' })[];
  message_pillars: (Basis & { message: string; proof_points: string[] })[];
  voice: Basis & { tone: string[]; language: string; use_words: string[]; avoid_words: string[] };
  channels: (Basis & { platform: string; role: string; budget_share_pct: number })[];
  markets?: (Basis & { name: string; role: 'start' | 'later' })[];
  channel_evaluation?: ChannelCheck[];
  creative_approach?: CreativeApproach;
  past_results?: { use: 'use' | 'weak' | 'ignore' | 'none'; reason: string };
  revision_notes?: string[];          // what changed for the owner's comments
  owner_feedback?: OwnerComment[];    // the owner's comments so far (carried into later versions)
  goals: Basis & { primary_goal: string; kpi: string; optimise_for?: string; volume_check?: string; first_target: string; timeframe_days: number };
  assumptions: { statement: string; how_to_test: string }[];
  open_questions: { question: string; why_it_matters: string }[];
}

export type SectionKey = Exclude<keyof StrategyContent, 'channel_evaluation'>;

export interface Strategy {
  id: string;
  version: number;
  status: 'proposed' | 'approved' | 'superseded';
  knowledge_version: number;
  content: StrategyContent;
  section_status: Record<string, 'proposed' | 'approved' | 'edited'>;
  edited: boolean;
  approved_at: string | null;
  created_at: string | null;
  stale: boolean | null;
  /** Why it is out of date: the brand's facts changed, or only Admeru's strategy writer improved. */
  stale_reason?: 'facts' | 'writer' | 'missing' | null;
  /** The approved version still in use while this newer one waits for approval. */
  in_use_version?: number | null;
}

export interface OnboardingState {
  onboarding_status: 'not_started' | 'in_review' | 'confirmed';
  knowledge_version: number;
  readiness: {
    ready_for_strategy: boolean; missing: Question[]; pending_confirmations: number; critical_pending: number;
    steps?: { id: string; title: string }[];
  };
  brand: { entities: Record<string, EntityItem[]> };
  pending: PendingFact[];
  strategy: Strategy | null;
  /** The latest quarterly review of the approved strategy against recent months' results. */
  quarterly_review?: QuarterlyReview | null;
  /** The owner can message customers but Meta has no customer list: ads can't reach them yet. */
  customer_list_gap?: { contacts: string; meta_connected: boolean } | null;
  import?: { accepted: number; proposed: number; warning: string };
}

export interface QuarterlyReview {
  at: string; months: string[]; verdict: 'holds' | 'adjust' | 'rethink' | 'too_little_data'; summary: string;
  changes: { id: string; section: SectionKey; change: string; evidence: string; months: string[]; status: 'open' | 'applied' | 'dismissed' }[];
}

export interface AnswerInput {
  entity_type: string;
  attribute: string;
  value: any;
  entity_key?: string | null;
}

export interface PlanWhy { reason: string; confidence: 'high' | 'medium' | 'low'; refs: string[]; fact_ids?: string[] }
export interface KeyDate { ref: string; date: string; name: string; confirm: boolean }

export interface PlanBrief extends PlanWhy {
  name: string; channel: string; objective: string;
  own?: boolean; own_note?: string | null;          // the owner's own campaign, added to the plan
  offering_ref: string; segment_ref: string; pillar_ref: string;
  offering?: string; segment?: string; message?: string;
  start_date: string; end_date: string; budget: number;
  hypothesis: string; creative_angle: string; call_to_action: string;
  key_date_ref?: string | null; key_date?: KeyDate | null; target_cost_per_result?: number | null;
  // How the ads are made (campaign production follows these)
  creative_route?: CreativeRoute | null;
  deliverables?: { format: string; count: number; motion?: boolean }[];
  asset_refs?: string[];
  assets?: { ref: string; id: string; media_asset_id: string; kind: MediaKind; label: string; url: string; format?: string | null; use_as_is: boolean }[];
  /** Who it reaches: new people, people who visited or engaged, or past customers. */
  audience?: 'new' | 'retarget' | 'customers';
  /** T# of this month's running campaign the brief keeps going (no new ads). */
  continues?: string | null;
  continue_of?: ContinueOf | null;
}

/** The running campaign a brief keeps going, with its results when the plan was made. */
export interface ContinueOf {
  ref: string; id: string; platform: string; channel: string; name: string; objective: string;
  spend: number; results: number; result_type: string; cost_per_result: number | null; brief?: string | null;
}

/** Where keeping a campaign running stands: confirmed (at), end date extended, budget set, or an error. */
export interface ContinueState { at?: string; extended?: string; budget?: string; error?: string | null }

export type BriefVerdict = 'not_started' | 'no_data' | 'too_early' | 'on_track' | 'behind';

/** One brief against its plan, as Admeru scores it (plan/results.scorecard). */
export interface ScoreRow {
  ref: string; brief_id: string; name: string; channel: string; objective: string; hypothesis: string;
  budget: number; start_date: string; end_date: string; days: number; days_run: number;
  out: boolean; continues: boolean; live: boolean;
  spend: number; expected_spend: number; results: number; result_type: string | null;
  cost_per_result: number | null; target: number | null;
  verdict: BriefVerdict; pace: 'under' | 'ok' | 'over' | null;
  /** The cost per result above which each result loses money (from the owner's sale value and margin). */
  break_even?: number | null;
  /** From the Leads inbox: leads judged, and how many were qualified or won. */
  lead_quality?: { leads: number; good: number; won: number; lost: number; decided: number; good_rate: number | null } | null;
  cost_per_good?: number | null;
  /** Cost per result weighed with the brand's past results on the channel: a likely range (middle 80%),
   * and the chance it is at or under the bar the verdict uses (estimates.py). */
  estimate?: { cost_per_result: number; low: number; high: number; results_seen: number; bar?: number; chance?: number;
    prior: { cost_per_result: number; weight: number; source: string } | null } | null;
  /** Results by the brief's end, a likely range. */
  forecast?: ResultRange | null;
}
export interface ResultRange { low: number; mid: number; high: number }
export interface Scorecard {
  as_of: string; month: string; currency: string; briefs: ScoreRow[];
  totals: { budget: number; expected_spend: number; spend: number; results: number; cost_per_result: number | null; forecast?: ResultRange | null };
  too_early: boolean;
  focus: { objective: string; kpi: string }[];
}

export interface CheckinProposal {
  id: string; action: 'rewrite' | 'skip' | 'pause' | 'shift_budget';
  ref: string; brief_id: string; brief_name: string; to_ref?: string | null; to_brief_id?: string | null; to_brief_name?: string | null;
  share_pct?: number | null; direction?: string; why: string;
  status: 'open' | 'applied' | 'dismissed'; done_at?: string; moved_per_day?: number;
}
/** A checkpoint check-in: is the plan working, and one-click changes (plan/checkins.py). */
export interface Checkin {
  index: number; at: string; date: string; check: string; if_not: string;
  verdict: 'on_track' | 'off_track' | 'too_early'; summary: string;
  briefs: { ref: string; brief_id: string; name: string; score: BriefVerdict; hypothesis: 'holds' | 'fails' | 'too_early' | 'no_data'; note: string }[];
  proposals: CheckinProposal[];
}

export interface LandingCheck {
  url: string; final_url: string; at: string; status: 'ok' | 'warn' | 'bad';
  checks: { key: string; ok: boolean; text: string; fix: string; severity: 'warn' | 'bad' | null }[];
  review?: { verdict: 'good' | 'fix_first' | 'weak'; summary: string; fixes: string[] } | null;
  /** Campaigns whose ads point at this page. */
  briefs?: string[];
  /** Read as a phone's browser renders it (false: raw HTML only, the browser wasn't available). */
  rendered?: boolean;
  /** Why there is no model review: the page builds itself in the browser. */
  review_skipped?: 'built_in_browser' | null;
}
export interface PostsGroup { name: string; posts: number; avg_reach: number; engagement_rate: number | null }
export interface PostsResults { posts: number; reach: number; message: PostsGroup[]; format: PostsGroup[] }
export interface FreshAds {
  id: string; status: 'queued' | 'running' | 'ready' | 'failed' | 'paused' | 'needs_input';
  error?: string | null; ads: { url: string; variant: number }[];
  added?: { at: string; ads: string[]; paused: string[] } | null; done?: number; total?: number;
}
export interface WeeklyFlag { brief_id: string; name: string; channel: string; kind: 'behind' | 'no_results' | 'pace' | 'fatigue' | 'low_quality'; text: string; frequency?: number; ctr_drop?: number }
export interface Lesson { month: string; lesson: string; evidence: string; next_time: string; confidence: 'high' | 'medium' | 'low' }
/** How a month went against its plan (plan/recap.py). */
export interface MonthRecap {
  at: string; headline: string; verdict: 'worked' | 'mixed' | 'did_not_work' | 'too_little_data';
  worked: { text: string; refs: string[]; names: string[] }[];
  did_not_work: { text: string; refs: string[]; names: string[] }[];
  lessons: (Lesson & { refs: string[]; names: string[] })[];
  totals?: Scorecard['totals'] | null; currency?: string | null;
}

export type CreativeRoute = 'ad_studio_photo' | 'ad_studio_scratch' | 'own_ad' | 'dynamic_creative' | 'search_text';
export type MediaKind = 'product_photo' | 'finished_ad' | 'video' | 'lifestyle' | 'logo' | 'other';

/** One of the brand's own photos, ads or videos in "Products & media". */
export interface MediaItem {
  id: string; media_asset_id: string; kind: MediaKind; kind_label: string; label: string;
  offering_keys: string[]; use_as_is: boolean; source: string; url: string;
  media_type: 'image' | 'video'; width?: number | null; height?: number | null; format?: string | null;
  duration_seconds?: number | null;
}
export interface MediaCandidate {
  media_asset_id: string; url: string; media_type: 'image' | 'video'; format?: string | null;
  source: string; suggested_kind: MediaKind; label: string;
}
export interface MediaState {
  items: MediaItem[];
  products: { key: string; name: string }[];
  kinds: { id: MediaKind; label: string }[];
}

export interface CreativeApproach extends Basis {
  channels: { platform: string; style: 'finished_ad' | 'dynamic_creative' | 'search_text'; formats: string[]; motion?: boolean }[];
  variants_per_campaign: number;
  use_own_media: string;
  style_notes?: string;
}

export interface PlanContent {
  summary: PlanWhy & { value: string };
  focus: (PlanWhy & { objective: string; kpi: string })[];
  briefs: PlanBrief[];
  organic_posts: { date: string; time?: string; channel: string; pillar_ref: string; message?: string; format: string; idea: string; key_date?: KeyDate | null }[];
  checkpoints: { date: string; check: string; if_not: string }[];
  /** Email / WhatsApp messages the owner sends themselves; Admeru drafts them. */
  followups?: { date: string; channel: 'email' | 'whatsapp'; audience: 'new_leads' | 'not_converted' | 'customers'; pillar_ref: string; message?: string; purpose: string; idea: string; key_date?: KeyDate | null }[];
  open_questions: { question: string; why_it_matters: string }[];
  revision_notes?: string[];          // what changed for the owner's comments
  pin_answers?: { ref: string; how: string; pin?: Pin | null }[];   // how the plan handles each pin
}

/** A campaign made from a monthly plan, as the Campaigns list shows it. */
export interface PlanCampaignRef {
  campaign_id: string; month: string; brief_id: string; name: string; channel: string; objective: string;
  start_date: string; end_date: string; budget: number; own: boolean; state: ItemState | string;
}

/** past: read only · plan: this month and next · ahead: posts and pins · later: nothing yet. */
export type MonthMode = 'past' | 'plan' | 'ahead' | 'later';

export type PinKind = 'sale' | 'event' | 'launch' | 'idea' | 'avoid';

/** Something the owner doesn't want to miss in a month ahead: a day, a few days, or the whole month. */
export interface Pin {
  id: string; month: string; text: string; kind: PinKind | null;
  start_date: string | null; end_date: string | null; when: string; created_at?: string | null;
}

export interface PlanContext {
  month: string; start: string; end: string;
  stage: 'start' | 'learn' | 'grow'; stage_reason: string;
  currency: string; monthly_budget: number; budget: number;
  channel_budgets: Record<string, number>; organic: string[];
  connected: string[]; not_connected: string[];
  key_dates: KeyDate[]; notes: string[];
  lead_in?: string | null;            // a month not started yet: early campaigns may start from here
  /** Who each paid channel can reach now (Admeru decides): retargeting and past customers. */
  audiences?: Record<string, { retarget: { ready: boolean; why: string }; customers: { ready: boolean; why: string } }>;
  /** A channel's own stage, where it differs from the brand's. */
  channel_stages?: Record<string, { stage: 'start' | 'learn' | 'grow'; reason: string }>;
}

export type BriefStatus = 'suggested' | 'approved' | 'skipped' | 'draft' | 'stale';

export interface PlanBriefRow {
  id: string; key: string; status: BriefStatus; version: number;
  brief: PlanBrief & { based_on?: { kind: string; text: string }[] };
  versions: { number: number; origin: 'generated' | 'edited' | 'regenerated' | 'budget_moved'; note?: string | null; name: string; at?: string }[];
  campaign_id: string | null; campaign_version: number | null;
}

export interface ChannelOffPreview {
  result: 'updated' | 'needs_refresh' | 'not_in_strategy';
  consequence: string;
  channels: { platform: string; budget_share_pct: number }[];
}

export interface MonthlyPlan {
  id: string; month: string; version: number; status: 'proposed' | 'approved' | 'superseded';
  strategy_id: string; strategy_version: number;
  context: PlanContext; plan: PlanContent; stale: string | null;
  briefs: PlanBriefRow[];
  budgets: Record<string, { budget: number; planned: number; approved: number }>;
  posts_status: 'suggested' | 'approved';
  posts?: PlanPostRow[];
  item_states?: Record<string, ItemState>;
  auto_produce?: { on: boolean; credits?: number; items?: number; at?: string } | null;
  posts_accounts?: PostAccount[];
  owner_feedback?: OwnerComment[];
  /** What changed since the plan was made, and the items it touches (plan/changes.py). */
  review?: PlanReview | null;
  /** On a new version: items already made that stay as they are when it is approved. */
  kept?: { kind: 'campaign' | 'post'; id: string; name: string; channel: string; date: string; budget?: number }[];
  /** Briefs that keep a running campaign going: {brief id: state}. */
  continued?: Record<string, ContinueState>;
  /** Checkpoint check-ins written so far, oldest first. */
  checkins?: Checkin[];
  /** Plan against actual for the month under way (approved plans only). */
  scorecard?: Scorecard | null;
  /** This week's look at the plan: things to know before the next check-in. */
  weekly?: { week: string; at: string; flags: WeeklyFlag[] } | null;
  /** Drafted follow-ups by their index in plan.followups. */
  followup_drafts?: Record<string, { subject: string; body: string; at: string; channel: 'email' | 'whatsapp' }>;
  /** The page the ads send people to, checked against this plan (landing.py). */
  landing?: LandingCheck | null;
  /** Every page the plan's ads send people to (the brand's site and each campaign's own page). */
  landings?: LandingCheck[];
  /** How the brand's recent organic posts did (last 90 days), by message and format. */
  posts_results?: PostsResults | null;
  /** Fresh Google ad text to paste, by brief id. */
  fresh_text?: Record<string, { headlines: string[]; descriptions: string[]; at: string }>;
  /** Fresh ads for live, tiring campaigns: {brief id: the latest}. */
  refreshes?: Record<string, FreshAds>;
  /** The month-end review, once the month is over. */
  recap?: MonthRecap | null;
  /** Lessons from earlier months' reviews that this plan was given, newest first. */
  lessons?: Lesson[];
  /** Admeru drafted this a week ahead, on its own. */
  drafted?: { at: string } | null;
  /** Posts planned ahead; the month's campaigns are planned a week before it starts. */
  posts_only?: boolean;
  approved_at: string | null; created_at: string | null;
}

export type ReviewAction = 'rewrite' | 'edit' | 'note' | 'change' | 'caption' | 'idea' | 'publish' | 'launch' | 'pause' | 'keep' | 'skip';

export interface ReviewItem {
  id: string; kind: 'campaign' | 'post'; name: string; state: ItemState | 'held'; date: string;
  reasons: string[]; keys: string[]; suggestion: string; options: ReviewAction[];
  held: boolean; hold_needed: boolean; remake: boolean;
  production_id: string | null; campaign_id: string | null;
}

export interface BudgetRow {
  brief_id: string; name: string; channel: string; running: boolean; budget: number; days_left: number;
  end_date: string; campaign_id: string | null; change: number; after: number;
  daily_change?: number; big?: boolean;
}

export interface PlanReview {
  plan_id: string;          // the plan in use (the approved one while a new version waits)
  changes: string[];
  items: ReviewItem[];
  /** Pins added or changed after the plan was made: add them (a revised version) or leave the plan. */
  pins: { id: string; text: string; when: string; kind: PinKind | null; changed: boolean; asked: string | null }[];
  budget: {
    old: number; new: number; currency: string; text: string; plan_change: number;
    running: BudgetRow[];
    future_only: { campaigns: BudgetRow[]; unassigned: number };
    with_running: { campaigns: BudgetRow[]; unassigned: number };
  } | null;
}

export interface PlanState {
  status: 'needs_brand' | 'needs_strategy' | 'ready';
  message: string;
  months: string[];
  month: string;
  suggested_month?: string;   // the month to plan: this one while campaigns have time to learn, else next
  late?: boolean;             // fewer than 10 days left in this month: campaigns go in next month's plan
  days_left?: number;
  strategy: {
    id: string; version: number; stale: boolean; stale_reason?: 'facts' | 'writer' | 'missing' | null;
    follows?: {   // what the plan inherits from the approved strategy
      channels: { platform: string; share: number; organic: boolean }[];
      start_markets: string[]; optimise_for: string;
      past_results?: { use: string; reason: string } | null;
    };
  } | null;
  context: PlanContext | null;
  plan: MonthlyPlan | null;
  owner_key_dates?: string[];
  options?: {
    segments: { ref: string; name: string }[]; messages: { ref: string; name: string }[]; offerings: { ref: string; name: string }[];
    all_offerings?: { ref: string; name: string; lead: boolean }[];   // an owner's own campaign may use any
    paid_channels?: string[];
  };
  warnings?: string[];
  moved?: Record<string, number>;
  pins?: Pin[];                 // this month's pins
  pin_months?: string[];        // months pins can go on (this one and three ahead)
  can_plan?: boolean;           // false: too far ahead to plan, pins only
  month_mode?: MonthMode;       // what the month allows
  pins_from?: string;           // a month further ahead takes pins from this day
  drafts_on?: string;           // Admeru drafts this month's plan on this day
  channel_off?: ChannelOffPreview & { strategy: Strategy | null };
}

export const brandKnowledgeApi = {
  getOnboarding: async (): Promise<OnboardingState> =>
    (await apiClient.get(`${BASE}/onboarding`)).data,

  /** Store what Brand Kit's website reader found (no second read of the site). */
  /** Start reading the website in the background; returns the read already running, if any. */
  startWebsiteRead: async (url: string): Promise<WebsiteRead> =>
    (await apiClient.post(`${BASE}/onboarding/website-read`, { url })).data.job,

  /** The latest website read: running, failed (with the reason) or done (with what it found). */
  getWebsiteRead: async (): Promise<WebsiteRead | null> =>
    (await apiClient.get(`${BASE}/onboarding/website-read`)).data.job,

  /** The website disagreed with the owner's answers: use it for everything, pick one by one, or discard it. */
  decideWebsiteRead: async (jobId: string, choice: WebsiteDecision): Promise<OnboardingState> =>
    (await apiClient.post(`${BASE}/onboarding/website-read/${jobId}/decision`, { choice })).data,

  ingestWebsite: async (url: string, extraction: Record<string, any>): Promise<OnboardingState> =>
    (await apiClient.post(`${BASE}/onboarding/website-extraction`, { url, extraction })).data,

  answer: async (answers: AnswerInput[], newEntities: Record<string, string[]> = {}): Promise<OnboardingState> =>
    (await apiClient.post(`${BASE}/onboarding/answers`, { answers, new_entities: newEntities })).data,

  complete: async (): Promise<OnboardingState> =>
    (await apiClient.post(`${BASE}/onboarding/complete`)).data,

  confirmFact: async (factId: string, value?: any): Promise<OnboardingState> =>
    (await apiClient.post(`${BASE}/facts/${factId}/confirm`, { value: value ?? null })).data,

  rejectFact: async (factId: string): Promise<OnboardingState> =>
    (await apiClient.post(`${BASE}/facts/${factId}/reject`)).data,

  removeEntity: async (entityType: string, key: string): Promise<OnboardingState> =>
    (await apiClient.delete(`${BASE}/entities/${entityType}/${encodeURIComponent(key)}`)).data,

  getStrategy: async (): Promise<Strategy | null> =>
    (await apiClient.get(`${BASE}/strategy`)).data.strategy,

  /** Starts the strategy in the background, waits for it, returns it. */
  proposeStrategy: async (newVariant = false): Promise<Strategy> => {
    const { job } = (await apiClient.post(`${BASE}/strategy/propose`, { new_variant: newVariant })).data;
    await brandKnowledgeApi.waitForJob(job);
    return (await brandKnowledgeApi.getStrategy()) as Strategy;
  },

  // ── Products & media ──
  getMedia: async (): Promise<MediaState> => (await apiClient.get(`${BASE}/media`)).data,
  getMediaCandidates: async (): Promise<MediaCandidate[]> =>
    (await apiClient.get(`${BASE}/media/candidates`)).data.candidates,
  addMedia: async (mediaAssetId: string, fields: Partial<Pick<MediaItem, 'kind' | 'label' | 'offering_keys' | 'use_as_is'>> = {}): Promise<MediaState> =>
    (await apiClient.post(`${BASE}/media`, { media_asset_id: mediaAssetId, ...fields })).data,
  changeMedia: async (id: string, fields: Partial<Pick<MediaItem, 'kind' | 'label' | 'offering_keys' | 'use_as_is'>>): Promise<MediaState> =>
    (await apiClient.patch(`${BASE}/media/${id}`, fields)).data,
  removeMedia: async (id: string): Promise<MediaState> => (await apiClient.delete(`${BASE}/media/${id}`)).data,
  /** Upload a file to the media library and add it to Products & media. */
  uploadMedia: async (file: File): Promise<MediaState> => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post('/api/v1/media/upload', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
    return brandKnowledgeApi.addMedia(data.asset.id);
  },

  /** Import an image from a link into the media library and add it to planning. */
  importMediaUrl: async (url: string): Promise<MediaState> => {
    const { data } = await apiClient.post('/api/v1/media/import-url', { url }, { timeout: 60000 });
    return brandKnowledgeApi.addMedia(data.asset.id);
  },

  // ── Campaign production ──
  productionChecklist: async (planId: string): Promise<ProductionChecklist> =>
    (await apiClient.get(`${BASE}/plan/${planId}/production/checklist`)).data,
  /** Start production. Each id list on its own: undefined = everything eligible, [] = none. */
  startProduction: async (planId: string, media: Record<string, MediaChoice>, briefIds?: string[], postIds?: string[]): Promise<Production[]> =>
    (await apiClient.post(`${BASE}/plan/${planId}/production`, { brief_ids: briefIds ?? null, post_ids: postIds ?? null, media })).data.productions,
  getProduction: async (planId: string): Promise<{ productions: Production[]; preparable: number }> =>
    (await apiClient.get(`${BASE}/plan/${planId}/production`)).data,
  answerProduction: async (id: string, value: unknown): Promise<Production[]> =>
    (await apiClient.post(`${BASE}/production/${id}/answer`, { value })).data.productions,
  noteProduction: async (id: string, text: string): Promise<Production[]> =>
    (await apiClient.post(`${BASE}/production/${id}/note`, { text })).data.productions,
  controlProduction: async (id: string, action: 'pause' | 'resume' | 'cancel' | 'retry'): Promise<Production[]> =>
    (await apiClient.post(`${BASE}/production/${id}/${action}`)).data.productions,
  // Review: the campaign as it will run, and changes before approving it
  reviewProduction: async (id: string): Promise<ProductionReview> =>
    (await apiClient.get(`${BASE}/production/${id}/review`)).data,
  askForChanges: async (id: string, text: string, remakeAds: boolean): Promise<Production[]> =>
    (await apiClient.post(`${BASE}/production/${id}/changes`, { text, remake_ads: remakeAds })).data.productions,
  regenerateAd: async (id: string, concept: string, option: ReviewOption['id'], direction = ''): Promise<Production[]> =>
    (await apiClient.post(`${BASE}/production/${id}/ads/${concept}/regenerate`, { option, direction })).data.productions,
  replaceAd: async (id: string, concept: string, body: { format: string; url: string; media_asset_id?: string; edited?: boolean }): Promise<ProductionReview> =>
    (await apiClient.post(`${BASE}/production/${id}/ads/${concept}/replace`, body)).data,
  /** Approve a campaign's ads. A start date that has passed answers 409 {code: 'late_start', message, days}
   *  until called again with confirmLate. */
  approveProduction: async (id: string, confirmLate = false): Promise<{ launch: { type: 'now' | 'scheduled'; at: string | null; days: number }; campaign_id: string }> =>
    (await apiClient.post(`${BASE}/production/${id}/approve`, { confirm_late: confirmLate })).data,
  // Posts: review, caption, schedule
  reviewPost: async (id: string): Promise<PostReview> =>
    (await apiClient.get(`${BASE}/production/${id}/review`)).data,
  redoPost: async (id: string, option: 'idea' | 'caption', direction = ''): Promise<Production[]> =>
    (await apiClient.post(`${BASE}/production/${id}/post/redo`, { option, direction })).data.productions,
  setPostCaption: async (id: string, text: string): Promise<PostReview> =>
    (await apiClient.put(`${BASE}/production/${id}/post/caption`, { text })).data,
  /** Approve a made post: it is scheduled on its date to the month's account for its channel, or
   *  (now) posted within a minute or two. */
  schedulePost: async (id: string, now = false): Promise<PostReview> =>
    (await apiClient.post(`${BASE}/production/${id}/post/approve`, { timezone: localZone(), now })).data,
  unschedulePost: async (id: string): Promise<PostReview> =>
    (await apiClient.post(`${BASE}/production/${id}/post/unschedule`)).data,
  setPostsAccounts: async (planId: string, accounts: PostAccount[]): Promise<PlanState> =>
    (await apiClient.put(`${BASE}/plan/${planId}/posts-accounts`, { accounts })).data,
  /** Where the month's posts go, planned or own (YYYY-MM). */
  setMonthPostsAccounts: async (month: string, accounts: PostAccount[]): Promise<OwnPostsMonth> =>
    (await apiClient.put(`${BASE}/posts/accounts`, { accounts }, { params: { month } })).data,

  // The owner's own posts: defined by them, in no plan until added to the month's approved plan
  ownPosts: async (month: string): Promise<OwnPostsMonth> =>
    (await apiClient.get(`${BASE}/posts`, { params: { month } })).data,
  createOwnPost: async (form: OwnPostForm): Promise<OwnPostsMonth & { post: OwnPost }> =>
    (await apiClient.post(`${BASE}/posts`, form)).data,
  ownPostEstimate: async (channel: string, format: string, slides = 0): Promise<Record<MediaMode, { ads: number; credits: number; minutes: number; note: string }>> =>
    (await apiClient.get(`${BASE}/posts/estimate`, { params: { channel, format, slides } })).data,
  ownPostChecklist: async (id: string): Promise<PostChecklist> =>
    (await apiClient.get(`${BASE}/posts/${id}/checklist`)).data,
  /** Make one post now (own or planned). */
  makePost: async (id: string, choice: MediaChoice, postNow = false): Promise<{ post: OwnPost; production: Production }> =>
    (await apiClient.post(`${BASE}/posts/${id}/make`, { ...choice, post_now: postNow ? localZone() : null })).data,
  attachOwnPost: async (id: string): Promise<OwnPostsMonth & { post: OwnPost }> =>
    (await apiClient.post(`${BASE}/posts/${id}/attach`)).data,
  detachOwnPost: async (id: string): Promise<OwnPostsMonth & { post: OwnPost }> =>
    (await apiClient.post(`${BASE}/posts/${id}/detach`)).data,
  skipOwnPost: async (id: string): Promise<OwnPostsMonth & { post: OwnPost }> =>
    (await apiClient.post(`${BASE}/posts/${id}/skip`)).data,
  restoreOwnPost: async (id: string): Promise<OwnPostsMonth & { post: OwnPost }> =>
    (await apiClient.post(`${BASE}/posts/${id}/restore`)).data,
  /** Instagram accounts linked to a Meta connection's Pages (organic posts go out as these). */
  instagramAccounts: async (connectionId: string): Promise<{ account_id: string; username: string; page_id: string; page_name: string; is_default: boolean }[]> =>
    (await apiClient.get(`/api/v1/publisher/instagram-accounts/${connectionId}`)).data.accounts,
  includeAd: async (id: string, concept: string, included: boolean): Promise<ProductionReview> =>
    (await apiClient.post(`${BASE}/production/${id}/ads/${concept}/include`, { included })).data,

  /** Revises the strategy for the owner's comments (in the background), returns the new version. */
  draftFollowUp: async (planId: string, index: number, note = ''): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/${planId}/followups/${index}/draft`, { note })).data,

  /** Check the page the ads send people to against this plan. */
  checkLanding: async (planId: string, url?: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/${planId}/landing-check`, url ? { url } : {})).data,

  /** Look at the competitors the owner named; findings wait in Your business for confirmation. */
  runResearch: async (): Promise<{ stored: number; proposed: number; sources: { name: string; enabled: boolean }[] }> =>
    (await apiClient.post(`${BASE}/research/run`)).data,

  /** Review the approved strategy against recent months' results now (in the background). */
  runQuarterlyReview: async (): Promise<void> => {
    const { job } = (await apiClient.post(`${BASE}/strategy/quarterly-review/run`)).data;
    await brandKnowledgeApi.waitForJob(job);
  },

  /** Revise the strategy with the review's changes the owner picked; returns once the new version is ready. */
  applyQuarterlyReview: async (changeIds: string[]): Promise<Strategy> => {
    const { job } = (await apiClient.post(`${BASE}/strategy/quarterly-review/apply`, { change_ids: changeIds })).data;
    await brandKnowledgeApi.waitForJob(job);
    return (await brandKnowledgeApi.getStrategy()) as Strategy;
  },

  dismissQuarterlyReview: async (changeIds: string[]): Promise<void> => {
    await apiClient.post(`${BASE}/strategy/quarterly-review/dismiss`, { change_ids: changeIds });
  },

  reviseStrategy: async (strategyId: string, comments: string): Promise<Strategy> => {
    const { job } = (await apiClient.post(`${BASE}/strategy/${strategyId}/revise`, { comments })).data;
    await brandKnowledgeApi.waitForJob(job);
    return (await brandKnowledgeApi.getStrategy()) as Strategy;
  },

  /** Revises the month's plan for the owner's comments (in the background), returns the plan state. */
  revisePlan: async (planId: string, month: string, comments: string): Promise<PlanState> => {
    const { job } = (await apiClient.post(`${BASE}/plan/${planId}/revise`, { comments })).data;
    await brandKnowledgeApi.waitForJob(job);
    return brandKnowledgeApi.getPlan(month);
  },

  /** The running strategy / plan job, if any (a page opened mid-generation keeps waiting). */
  getActiveJob: async (kind: 'strategy' | 'plan'): Promise<BrandJob | null> =>
    (await apiClient.get(`${BASE}/jobs/active`, { params: { kind } })).data.job,

  /** Polls a background job until it is done; throws with the job's message when it failed. */
  waitForJob: async (job: BrandJob, everyMs = 3000, maxMs = 15 * 60 * 1000): Promise<BrandJob> => {
    const until = Date.now() + maxMs;
    let current = job;
    while (current.status === 'queued' || current.status === 'running') {
      if (Date.now() > until) throw new Error('This is taking longer than expected. Please check back in a few minutes.');
      await new Promise(r => setTimeout(r, everyMs));
      current = (await apiClient.get(`${BASE}/jobs/${current.id}`)).data.job;
    }
    if (current.status === 'failed') throw new Error(current.error || 'Something went wrong. Please try again.');
    return current;
  },

  editSection: async (strategyId: string, section: SectionKey, value: any): Promise<Strategy> =>
    (await apiClient.put(`${BASE}/strategy/${strategyId}/sections/${section}`, { value })).data.strategy,

  approve: async (strategyId: string, sections?: SectionKey[]): Promise<Strategy> =>
    (await apiClient.post(`${BASE}/strategy/${strategyId}/approve`, { sections: sections ?? null })).data.strategy,

  getPlan: async (month?: string): Promise<PlanState> =>
    (await apiClient.get(`${BASE}/plan`, { params: month ? { month } : {} })).data,

  /** Starts the plan in the background, waits for it, returns the plan state. */
  proposePlan: async (month: string, newVariant = false, postsOnly = false): Promise<PlanState> => {
    const { job } = (await apiClient.post(`${BASE}/plan/propose`, { month, new_variant: newVariant, posts_only: postsOnly })).data;
    await brandKnowledgeApi.waitForJob(job);
    return brandKnowledgeApi.getPlan(month);
  },

  approvePlan: async (planId: string, autoProduce?: boolean): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/${planId}/approve`, autoProduce === undefined ? {} : { auto_produce: autoProduce })).data,

  /** Make each approved item a few days before its date (on), or only remind (off). */
  setAutoProduce: async (planId: string, on: boolean): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/${planId}/auto-produce`, { on })).data,

  /** Add the owner's own campaign to the month's plan; Admeru writes the brief from their choices. */
  addOwnCampaign: async (planId: string, form: {
    channel: string; objective: string; start_date: string; end_date: string; budget: number;
    offering_ref?: string | null; segment_ref?: string | null; pillar_ref?: string | null; note: string;
    budget_mode: 'from_plan' | 'on_top';
  }): Promise<PlanState & { added?: string }> =>
    (await apiClient.post(`${BASE}/plan/${planId}/briefs`, form, { timeout: 240000 })).data,
  /** The owner's one-click decision on an item a change touches (text: their own change). */
  decideReviewItem: async (planId: string, itemId: string, action: ReviewAction, text?: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/${planId}/review/items/${itemId}`,
      { action, text: text ?? null, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' },
      { timeout: 240000 })).data,
  /** Apply a monthly budget change: only campaigns not live yet, or running ones too
   *  (`applied`: the running campaigns whose platform budget was changed). */
  applyReviewBudget: async (planId: string, mode: 'future' | 'running', applied: string[] = []): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/${planId}/review/budget`, { mode, applied })).data,

  // ── Pins (this month and three ahead) ──
  listPins: async (): Promise<{ months: string[]; pins: Pin[] }> => (await apiClient.get(`${BASE}/pins`)).data,
  addPin: async (pin: { month: string; text: string; start_date?: string | null; end_date?: string | null; kind?: PinKind | null; every_year?: boolean }): Promise<{ pin: Pin; months: string[]; pins: Pin[] }> =>
    (await apiClient.post(`${BASE}/pins`, pin)).data,
  updatePin: async (id: string, changes: { text?: string; start_date?: string; end_date?: string; whole_month?: boolean; month?: string; kind?: PinKind | 'none' }): Promise<{ pin: Pin; months: string[]; pins: Pin[] }> =>
    (await apiClient.put(`${BASE}/pins/${id}`, changes)).data,
  removePin: async (id: string): Promise<{ removed: Pin; months: string[]; pins: Pin[] }> =>
    (await apiClient.delete(`${BASE}/pins/${id}`)).data,
  /** A pin added after its month's plan: add it (a revised version in the background) or leave the plan. */
  decideReviewPin: async (planId: string, pinId: string, action: 'add' | 'later'): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/${planId}/review/pins/${pinId}`, { action })).data,
  /** A fresh version from the owner's direction (in the background); this one stays until the new one is approved. */
  startOverPlan: async (planId: string, direction: string): Promise<BrandJob> =>
    (await apiClient.post(`${BASE}/plan/${planId}/start-over`, { direction })).data.job,

  approveBrief: async (id: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/briefs/${id}/approve`)).data,

  skipBrief: async (id: string, scope: 'month' | 'channel', moveBudget = false): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/briefs/${id}/skip`, { scope, move_budget: moveBudget })).data,

  /** Make fresh ads for a live campaign people have seen too often. */
  refreshBrief: async (briefId: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/briefs/${briefId}/refresh`)).data,

  freshAdText: async (briefId: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/briefs/${briefId}/fresh-text`)).data,

  addFreshAds: async (productionId: string, keepOld: boolean): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/production/${productionId}/refresh/add`, { keep_old: keepOld })).data,

  discardFreshAds: async (productionId: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/production/${productionId}/refresh/discard`)).data,

  /** Make one change a check-in suggested (rewrite / skip a brief, pause or move budget live). */
  applyCheckinChange: async (planId: string, index: number, proposalId: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/${planId}/checkins/${index}/proposals/${proposalId}/apply`)).data,

  dismissCheckinChange: async (planId: string, index: number, proposalId: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/${planId}/checkins/${index}/proposals/${proposalId}/dismiss`)).data,

  /** Keep the running campaign this brief continues going: extends it now, new budget from its start. */
  continueBrief: async (id: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/briefs/${id}/continue`)).data,

  restoreBrief: async (id: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/briefs/${id}/restore`)).data,

  editBrief: async (id: string, changes: Record<string, any>): Promise<PlanState> =>
    (await apiClient.put(`${BASE}/plan/briefs/${id}`, { changes })).data,

  regenerateBrief: async (id: string, note: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/briefs/${id}/regenerate`, { note }, { timeout: 240000 })).data,

  useBriefVersion: async (id: string, n: number): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/briefs/${id}/versions/${n}`)).data,

  linkBriefCampaign: async (id: string, campaignId: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/briefs/${id}/campaign`, { campaign_id: campaignId })).data,

  /** The canvas launched this campaign: a plan campaign's brief records the budget and dates it went out with. */
  syncBriefFromCampaign: async (campaignId: string, launched: { budget?: number; start_date?: string; days?: number }): Promise<{ brief: PlanBriefRow | null }> =>
    (await apiClient.post(`${BASE}/plan/briefs/by-campaign/${campaignId}/launched`, launched)).data,

  briefForCampaign: async (campaignId: string): Promise<(PlanBriefRow & { out_of_date: boolean }) | null> =>
    (await apiClient.get(`${BASE}/plan/briefs/by-campaign/${campaignId}`)).data.brief,

  approvePosts: async (planId: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/${planId}/posts/approve`)).data,
  /** Each month's posts container (a campaign conversation the scheduler posts from) and how its posts stand. */
  postsContainers: async (): Promise<{ conversation_id: string; month: string; plan_id: string | null; posts: number; states: Partial<Record<ItemState, number>> }[]> =>
    (await apiClient.get(`${BASE}/plan/posts-containers`)).data.containers,
  /** Every campaign made from a monthly plan, any month (for the Campaigns list). */
  planCampaigns: async (): Promise<PlanCampaignRef[]> =>
    (await apiClient.get(`${BASE}/plan/campaigns`)).data.campaigns,
  approvePlanPost: async (id: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/posts/${id}/approve`)).data,
  skipPost: async (id: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/posts/${id}/skip`)).data,
  restorePost: async (id: string): Promise<PlanState> =>
    (await apiClient.post(`${BASE}/plan/posts/${id}/restore`)).data,
  // The owner's timezone lets the server store a new time as the publish slot it goes out at
  editPost: async (id: string, changes: { date?: string; time?: string; idea?: string }): Promise<PlanState> =>
    (await apiClient.put(`${BASE}/plan/posts/${id}`, { ...changes, timezone: localZone() })).data,

  getChannels: async (): Promise<{ platform: string; label: string; off: boolean }[]> =>
    (await apiClient.get(`${BASE}/channels`)).data.channels,

  channelOffPreview: async (channel: string): Promise<ChannelOffPreview> =>
    (await apiClient.get(`${BASE}/channels/${channel}/off-preview`)).data,

  turnChannelOff: async (channel: string): Promise<ChannelOffPreview & { strategy: Strategy | null }> =>
    (await apiClient.post(`${BASE}/channels/${channel}/off`)).data,

  turnChannelOn: async (channel: string): Promise<{ off: string[] }> =>
    (await apiClient.post(`${BASE}/channels/${channel}/on`)).data,
};
